import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';

const HERO_BLOBS = [
  {
    color: 'rgba(242,101,34,0.55)',
    size: 640,
    top: '-160px',
    right: '-120px',
    animate: { x: [0, 40, -20, 0], y: [0, -30, 20, 0] },
    duration: 18,
  },
  {
    color: 'rgba(27,54,93,0.6)',
    size: 560,
    bottom: '-180px',
    left: '-140px',
    animate: { x: [0, -30, 25, 0], y: [0, 40, -15, 0] },
    duration: 22,
  },
  {
    color: 'rgba(247,179,43,0.45)',
    size: 480,
    top: '30%',
    left: '40%',
    animate: { x: [0, 30, -20, 0], y: [0, -20, 30, 0] },
    duration: 16,
  },
  {
    color: 'rgba(242,101,34,0.3)',
    size: 380,
    bottom: '10%',
    right: '5%',
    animate: { x: [0, -25, 15, 0], y: [0, 25, -20, 0] },
    duration: 20,
  },
];

const FOOTER_BLOBS = [
  {
    color: 'rgba(242,101,34,0.4)',
    size: 500,
    top: '-100px',
    left: '-80px',
    animate: { x: [0, 30, -10, 0], y: [0, -20, 15, 0] },
    duration: 17,
  },
  {
    color: 'rgba(247,179,43,0.35)',
    size: 420,
    bottom: '-80px',
    right: '-60px',
    animate: { x: [0, -20, 10, 0], y: [0, 20, -10, 0] },
    duration: 21,
  },
  {
    color: 'rgba(27,54,93,0.5)',
    size: 360,
    top: '40%',
    right: '30%',
    animate: { x: [0, 15, -15, 0], y: [0, -15, 15, 0] },
    duration: 14,
  },
];

/**
 * Decorative animated background blobs.
 * Parent must be position:relative; overflow:hidden.
 */
function FloatingBlobs({ variant }) {
  const theme = useTheme();
  const blobs = variant === 'footer' ? FOOTER_BLOBS : HERO_BLOBS;

  return (
    <>
      {blobs.map((blob, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: blob.top,
            bottom: blob.bottom,
            left: blob.left,
            right: blob.right,
            width: blob.size,
            height: blob.size,
            borderRadius: '50%',
            background: blob.color,
            filter: 'blur(80px)',
            opacity: theme.palette.mode === 'dark' ? 0.25 : 0.35,
            pointerEvents: 'none',
            zIndex: 0,
          }}
          component={motion.div}
          animate={blob.animate}
          transition={{
            duration: blob.duration,
            repeat: Infinity,
            repeatType: 'mirror',
            ease: 'easeInOut',
          }}
        />
      ))}
    </>
  );
}

FloatingBlobs.propTypes = {
  variant: PropTypes.oneOf(['hero', 'footer']),
};

FloatingBlobs.defaultProps = {
  variant: 'hero',
};

export default FloatingBlobs;
